import React, { CSSProperties, forwardRef, useImperativeHandle, useMemo } from "react";
import { ICommandBarItemProps, CommandBar as OfficeCommandBar } from "@fluentui/react";
import { domUtils, useTheme } from "cayo.ui";
import styled from "styled-components";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { ItemsPerPage } from "@cayo/shared/api/types/schema";
import { DispatchAction, IGridState } from "@cayo/shared/components/GridContainer/types";
import { UIInteraction } from "@cayo/shared/hooks/useUIInteraction";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import useCommandBar from "./hooks";
import { useFarItems } from "./useFarItems";
import { ICommandBar } from "../../api/schema.api";
import AnrSearchBox from "../AnrSearchBox";
import { GroupConfig } from "../GridContainer/GridFilterBar";
import useResetColumnsAndGroupButton from "../GridContainer/hooks/useResetColumnsAndGroupButton";

//const log = logger.getLogger("QUICK-FILTER-NEW CommandBar2");

export type CommandBarProps = {
  scheme: Omit<ICommandBar, "type">;
  dispatch?: DispatchAction;
  gridState?: IGridState;
  uiInteraction: UIInteraction;
  groupConfig?: GroupConfig[];
  showFilterBarItems: boolean;
  object?: Entity;
  style?: CSSProperties;
  signals?: IPropertiesPanelSignals;
  previewQueryId?: string | null;
  maxResults?: ItemsPerPage[];
  showSearchButton: boolean;
  isMainView?: boolean;
  ref?: React.ForwardedRef<{ itemsRef: React.RefObject<ICommandBarItemProps[]> }>;
};

const CommandBar = forwardRef<
  { itemsRef: React.RefObject<ICommandBarItemProps[]> },
  Omit<CommandBarProps, "ref">
>((props, ref) => {
  const { style, scheme, showSearchButton } = props;
  const { items: officeItems, itemsRef } = useCommandBar(props);
  const theme = useTheme();

  useImperativeHandle(ref, () => ({
    itemsRef,
  }));

  const styles = props.isMainView ? theme.elementStyles.commandBar : { paddingLeft: 0 };

  const resetBtn = useResetColumnsAndGroupButton({
    query: props?.gridState?.query || ({} as any),
    dispatch: props?.dispatch,
    groupConfig: props.groupConfig,
    hide: props.showFilterBarItems !== true,
  });

  const farItems = useFarItems(
    resetBtn,
    scheme.showSelectionCounter,
    props?.dispatch,
    props.gridState?.selectedItems
  );

  const searchBoxItem = useMemo<ICommandBarItemProps | null>(
    () =>
      props.showFilterBarItems && props.dispatch
        ? {
            key: "anr-search-box",
            onRender: () => (
              <SearchBoxWrapper>
                <AnrSearchBox
                  query={props.gridState?.query}
                  dispatch={props.dispatch!}
                  previewQueryId={props.previewQueryId}
                  width={200}
                  showSearchButton={showSearchButton}
                />
              </SearchBoxWrapper>
            ),
          }
        : null,
    [
      props.showFilterBarItems,
      props.dispatch,
      props.gridState?.query,
      props.previewQueryId,
      showSearchButton,
    ]
  );

  const allItems = useMemo(
    () => (searchBoxItem ? [searchBoxItem, ...officeItems] : officeItems),
    [searchBoxItem, officeItems]
  );

  if (!farItems?.length && !allItems?.length) {
    return <></>;
  }

  return (
    <CommandBarContainer additionalStyle={style}>
      <StyledOfficeCommandBar
        onMouseDown={domUtils.stopMouseDown}
        items={allItems}
        farItems={farItems}
        styles={{ root: { ...styles } }}
      />
    </CommandBarContainer>
  );
});

const StyledOfficeCommandBar = styled(OfficeCommandBar)`
  flex-grow: 1;
  min-width: 1px;
`;

const SearchBoxWrapper = styled.div.attrs({
  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
})`
  display: flex;
  align-items: center;
  height: 100%;
  margin-right: 8px;
`;

const CommandBarContainer = styled.div<{ additionalStyle?: CSSProperties }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  ${(props) => ({ ...props.additionalStyle })}
`;

CommandBar.displayName = "CommandBar";
export default CommandBar;
